"use client";

import { useEffect } from "react";

import { useDeferredReady } from "./_deferred/useDeferredReady";

/**
 * Loads and initialises PostHog only after the browser is idle or the user
 * interacts. posthog-js (~230 kB parsed) is imported *dynamically* inside the
 * effect — a static top-level import would pull the whole library into the root
 * layout's client chunk and parse it on every page's first load, which is the
 * single biggest slice of initial main-thread work / unused JS. No component
 * uses the `usePostHog` hook, so the React context provider isn't needed.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const ready = useDeferredReady();

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;

    import("posthog-js").then(({ default: posthog }) => {
      if (cancelled) return;
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN as string, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        defaults: "2026-05-30",
      });
    });

    return () => {
      cancelled = true;
    };
  }, [ready]);

  return <>{children}</>;
}
